import React from 'react';

export type IconType = 'info' | 'success' | 'warning' | 'error' | 'confirm';

/**
 * Centralized icon utility class for Font Awesome icons
 * Used across Modal, Toast, and Alert components
 */
export class IconUtils {
  /**
   * Get Font Awesome icon component based on type
   * @param type - Icon type (info, success, warning, error, confirm)
   * @param withWrapper - If true, wraps icon in styled span for toast context
   * @returns React element with Font Awesome icon
   */
  static getIcon(type: IconType, withWrapper: boolean = false): React.ReactNode {
    const faIconClassMap: Record<IconType, string> = {
      success: 'fa-check',
      info: 'fa-info',
      warning: 'fa-exclamation-triangle',
      error: 'fa-times',
      confirm: 'fa-question',
    };

    const colorMap: Record<IconType, string> = {
      success: 'text-green-500',
      info: 'text-blue-500',
      warning: 'text-yellow-500',
      error: 'text-red-500',
      confirm: 'text-blue-500',
    };

    // When wrapped, icon should be white; otherwise, use color map
    const iconClassName = withWrapper
      ? `fa ${faIconClassMap[type]} text-white text-sm`
      : `fa ${faIconClassMap[type]} ${colorMap[type]}`;

    const icon = React.createElement('i', {
      className: iconClassName,
      'aria-hidden': 'true',
    });

    if (withWrapper) {
      return React.createElement(
        'span',
        {
          className:
            'mr-2 flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full border-2 border-white text-white shadow-sm text-sm',
          style: { backgroundColor: this.getIconBgColor(type) },
        },
        icon
      );
    }

    return icon;
  }

  /**
   * Get background color for icon
   * @param type - Icon type
   * @returns Hex color value
   */
  private static getIconBgColor(type: IconType): string {
    const bgColorMap: Record<IconType, string> = {
      success: '#22c55e',
      info: '#3b82f6',
      warning: '#eab308',
      error: '#ef4444',
      confirm: '#3b82f6',
    };
    return bgColorMap[type];
  }

  /**
   * Get toast configuration (colors and styling)
   * @param type - Icon type
   * @returns Object with container and text classes
   */
  static getToastConfig(type: IconType): { containerClass: string; textClass: string } {
    const configMap: Record<IconType, { containerClass: string; textClass: string }> = {
      info: {
        containerClass: 'border-l-4 border-blue-500 bg-blue-50',
        textClass: 'text-base text-blue-700',
      },
      success: {
        containerClass: 'border-l-4 border-green-500 bg-green-50',
        textClass: 'text-base text-green-700',
      },
      warning: {
        containerClass: 'border-l-4 border-yellow-500 bg-yellow-50',
        textClass: 'text-base text-yellow-700',
      },
      error: {
        containerClass: 'border-l-4 border-red-500 bg-red-50',
        textClass: 'text-base text-red-700',
      },
      confirm: {
        containerClass: 'border-l-4 border-blue-500 bg-blue-50',
        textClass: 'text-base text-blue-700',
      },
    };
    return configMap[type];
  }

  /**
   * Get color based on type
   * @param type - Icon type
   * @returns CSS color value
   */
  static getIconColor(type: IconType): string {
    const colorMap: Record<IconType, string> = {
      success: '#22c55e',
      info: '#3b82f6',
      warning: '#eab308',
      error: '#ef4444',
      confirm: '#3b82f6',
    };
    return colorMap[type];
  }
}

export default IconUtils;
